import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { fetchExpertMentions, fetchExperts } from "@/lib/api";
import MentionCard from "@/components/MentionCard";
import InitialsAvatar from "@/components/InitialsAvatar";
import { DisclaimerBanner } from "@/components/Disclaimer";

export default function ExpertDetail() {
  const { id } = useParams();
  const [expert, setExpert] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [all, ms] = await Promise.all([
        fetchExperts(),
        fetchExpertMentions(id).catch(() => null),
      ]);
      const exp = all.find((e) => e.id === id);
      if (!exp) {
        setNotFound(true);
      } else {
        setExpert(exp);
        setMentions(ms || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (notFound) {
    return (
      <div data-testid="expert-not-found" className="py-12 text-center">
        <div className="text-lg font-semibold">Expert not tracked yet</div>
        <div className="mt-1 text-sm text-[#94a3b8]">
          Try one of the tracked voices from the{" "}
          <Link to="/experts" className="text-[#60a5fa]">experts directory</Link>.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="expert-detail-page">
      <Link
        to="/experts"
        className="inline-flex items-center gap-1.5 text-xs text-[#94a3b8] hover:text-[#f8fafc]"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All experts
      </Link>

      {expert && (
        <div className="mt-4 flex flex-col gap-4 border border-[#1e293b] bg-[#111827] p-4 md:flex-row md:items-start md:p-5">
          <div className="flex items-start gap-4 md:contents">
            <InitialsAvatar
              name={expert.name}
              expertId={expert.id}
              size="xl"
            />
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748b]">
                Tracked Voice
              </div>
              <h1 className="text-xl font-bold tracking-tight md:text-2xl">
                {expert.name}
              </h1>
              <div className="mt-0.5 text-sm text-[#94a3b8]">{expert.title}</div>
              <div className="mt-1 text-xs text-[#64748b]">{expert.focus}</div>
            </div>
          </div>
          <button
            data-testid="expert-share-btn"
            onClick={async () => {
              const url = `${window.location.origin}/expert/${expert.id}`;
              try {
                await navigator.clipboard.writeText(url);
              } catch {}
            }}
            className="inline-flex items-center justify-center gap-2 border border-[#1e293b] bg-[#111827] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#3b82f6] hover:text-[#60a5fa]"
          >
            Share profile
          </button>
        </div>
      )}

      <div className="mt-4">
        <DisclaimerBanner />
      </div>

      <h2 className="mb-3 mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-[#64748b]">
        Companies mentioned · {mentions.length}
      </h2>

      {loading ? (
        <div className="p-8 text-center text-sm text-[#64748b]">Loading…</div>
      ) : mentions.length === 0 ? (
        <div className="border border-[#1e293b] bg-[#111827] p-8 text-center text-sm text-[#64748b]">
          No verified mentions for {expert?.name || "this expert"} yet. Ingestion is running against their public sources.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {mentions.map((m) => (
            <MentionCard key={m.id} mention={m} hideExpert />
          ))}
        </div>
      )}
    </div>
  );
}
